import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { RGB, CopyButton, ConfirmButton, errMsg } from './ext/extUi';

// API page → Webhooks: signed webhooks for the admin's own matches (GET/POST/PATCH/DELETE
// /api/v1/webhooks…) — the same delivery as external tournaments' webhooks: every v1 event of your
// matches (optionally one season, optionally some event types), HMAC-signed, retried, with a delivery log.
// Styles: apg-* in api/apiPage.css (imported by ApiAccess.js).

const EVENT_TYPES = ['round_start', 'round_end', 'kill', 'suicide', 'bomb_plant', 'bomb_defuse_start', 'bomb_defuse', 'bomb_explode', 'score_update', 'map_start', 'map_end', 'match_end',
  'phase_change', 'pause_start', 'pause_end', 'halftime_start', 'halftime_end', 'side_switch', 'knife_winner', 'knife_decision', 'round_restored', 'map_reset',
  'ace', 'zeus_kill', 'knife_kill', 'clutch_start', 'clutch_won', 'clutch_lost', 'map_point', 'match_point', 'seating_update', 'player_connect', 'player_disconnect'];
// = services/v1/bracketEvents TYPES (opt-in on webhooks)
const BRACKET_EVENT_TYPES = ['bracket_created', 'bracket_status', 'bracket_rebuilt', 'bracket_deleted', 'bracket_matches_added', 'bracket_match_teams',
  'bracket_match_scheduled', 'bracket_match_unlinked', 'bracket_match_result', 'bracket_match_reset', 'bracket_match_needs_decision'];

const STATE = { delivered: 'ok', pending: 'warn', failed: 'bad', skipped: 'off' };

function Deliveries({ hook }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const load = useCallback(() => axios.get(`/api/v1/webhooks/${hook.id}/deliveries`, { params: { limit: 30 } }).then(r => setRows(r.data.data)).catch(() => setRows([])), [hook.id]);
  useEffect(() => { load(); const iv = setInterval(load, 5000); return () => clearInterval(iv); }, [load]);
  if (!rows) return <div className="apg-empty">{t('common.loading', 'Loading...')}</div>;
  if (!rows.length) return <div className="apg-empty">{t('webhooks.noDeliveries', 'No deliveries yet.')}</div>;
  return (
    <div className="apg-deliveries">
      {rows.map(d => {
        let type = '';
        try { type = JSON.parse(d.body).event.type; } catch (_) { type = (d.body.match(/"type":"([a-z_]+)"/) || [])[1] || ''; }
        return (
          <div key={d.id} className="apg-delivery">
            <span className="id">#{d.id}</span>
            <span className={`apg-state ${STATE[d.status] || 'off'}`}>{t(`webhooks.status.${d.status}`, d.status)}</span>
            <span className="type">{type}</span>
            {d.match_id && <span className="muted">{t('webhooks.matchN', 'match {{n}}', { n: d.match_id })}</span>}
            <span className="grow" />
            {d.last_error && <span className="bad" title={d.last_error}>{d.last_error.slice(0, 40)}</span>}
            <span className="muted">{t('webhooks.attempts', '{{n}} attempt(s)', { n: d.attempts })}</span>
          </div>
        );
      })}
    </div>
  );
}

function HookRow({ hook, seasons, onChanged }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [secret, setSecret] = useState(hook.secret || null);
  const [msg, setMsg] = useState('');
  const act = async (fn, okText) => { setMsg(''); try { const r = await fn(); if (r && r.data && r.data.secret) setSecret(r.data.secret); if (okText) setMsg(okText); onChanged(); } catch (e) { setMsg(errMsg(e, t('ext.saveFailed', 'Could not save'))); } };
  const season = seasons.find(s => s.id === hook.season_id);
  return (
    <div className="apg-row">
      <div className="apg-row-top">
        <span className={`apg-state ${hook.is_active ? 'ok' : 'off'}`}>{hook.is_active ? t('webhooks.active', 'Active') : t('webhooks.paused', 'Paused')}</span>
        <span className="apg-row-main apg-hook-url">{hook.url}</span>
      </div>
      <div className="apg-row-meta">
        <span>{season ? season.name : t('webhooks.allMyMatches', 'All my matches')}</span>
        <span>{hook.event_types.length ? t('webhooks.nTypes', '{{n}} event types', { n: hook.event_types.length }) : t('webhooks.allEvents', 'All events')}</span>
        <span>{t('webhooks.delivered', 'Delivered')}: {hook.deliveries.delivered}</span>
        <span>{t('webhooks.pending', 'Pending')}: {hook.deliveries.pending}</span>
        <span className={hook.deliveries.failed ? 'bad' : undefined}>{t('webhooks.failed', 'Failed')}: {hook.deliveries.failed}</span>
        {hook.last_error && <span className="bad">{t('webhooks.lastError', 'Last error')}: {hook.last_error}</span>}
      </div>
      {secret && (
        <div className="apg-secret">
          <span className="apg-label">{t('webhooks.secret', 'Signing secret')}</span>
          <code>{secret}</code>
          <CopyButton text={secret} small />
        </div>
      )}
      <div className="apg-actions">
        <button type="button" className="apg-btn" aria-expanded={open} onClick={() => setOpen(!open)}>{open ? t('webhooks.hideLog', 'Hide deliveries') : t('webhooks.showLog', 'Deliveries')}</button>
        <button type="button" className="apg-btn" onClick={() => act(() => axios.post(`/api/v1/webhooks/${hook.id}/test`), t('webhooks.testQueued', 'Test event queued'))}>{t('webhooks.test', 'Send test')}</button>
        {!secret && <button type="button" className="apg-btn" onClick={() => act(() => axios.get(`/api/v1/webhooks/${hook.id}`, { params: { reveal: 1 } }))}>{t('webhooks.reveal', 'Show secret')}</button>}
        {hook.deliveries.failed > 0 && <button type="button" className="apg-btn warn" onClick={() => act(() => axios.post(`/api/v1/webhooks/${hook.id}/redeliver`, {}), t('webhooks.requeued', 'Failed deliveries re-sent'))}>{t('webhooks.redeliver', 'Re-send failed')}</button>}
        <button type="button" className="apg-btn" onClick={() => act(() => axios.patch(`/api/v1/webhooks/${hook.id}`, { is_active: !hook.is_active }))}>{hook.is_active ? t('webhooks.pause', 'Pause') : t('webhooks.resume', 'Resume')}</button>
        <ConfirmButton label={t('webhooks.rotate', 'New secret')} color={RGB.amber} warning={t('webhooks.rotateWarning', 'The old secret stops working at once')} onConfirm={() => act(() => axios.post(`/api/v1/webhooks/${hook.id}/rotate-secret`))} />
        <ConfirmButton label={t('common.delete', 'Delete')} warning={t('webhooks.deleteWarning', 'Deletes the webhook and its delivery log')} onConfirm={() => act(() => axios.delete(`/api/v1/webhooks/${hook.id}`))} />
        {msg && <span className="apg-msg">{msg}</span>}
      </div>
      {open && <Deliveries hook={hook} />}
    </div>
  );
}

export default function ApiWebhooks() {
  const { t } = useTranslation();
  const [hooks, setHooks] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [form, setForm] = useState({ url: '', season_id: '', types: [] });
  const [err, setErr] = useState('');
  const [created, setCreated] = useState(null);

  const load = useCallback(() => axios.get('/api/v1/webhooks').then(r => setHooks(r.data.data)).catch(e => setErr(errMsg(e, t('ext.loadFailed', 'Failed to load')))), [t]);
  useEffect(() => { load(); axios.get('/api/v1/seasons').then(r => setSeasons(r.data.data || [])).catch(() => { }); }, [load]);

  const create = async () => {
    setErr('');
    try {
      const r = await axios.post('/api/v1/webhooks', { url: form.url, season_id: form.season_id || null, event_types: form.types });
      setCreated(r.data); setForm({ url: '', season_id: '', types: [] }); load();
    } catch (e) { setErr(errMsg(e, t('ext.saveFailed', 'Could not save'))); }
  };
  const toggleType = (ty) => setForm(f => ({ ...f, types: f.types.includes(ty) ? f.types.filter(x => x !== ty) : [...f.types, ty] }));
  const typeButton = (ty) => (
    <button key={ty} type="button" className={`apg-type${form.types.includes(ty) ? ' on' : ''}`} aria-pressed={form.types.includes(ty)} onClick={() => toggleType(ty)}>{ty}</button>
  );

  return (
    <section className="apg-sec">
      <div className="apg-sec-head"><h2>{t('webhooks.title', 'Webhooks')}</h2></div>
      <p className="apg-hint">{t('webhooks.hint', 'POST every event of your matches to your own URL (stage lights, overlays, bots). Each request is signed: X-Flux-Signature = sha256 HMAC of the body with the secret. Failed deliveries are retried after 1 s, 5 s, 30 s, 2 min and 10 min.')}</p>
      {hooks && hooks.length > 0 && (
        <div className="apg-list">
          {hooks.map(hk => <HookRow key={hk.id} hook={created && created.id === hk.id ? { ...hk, secret: created.secret } : hk} seasons={seasons} onChanged={load} />)}
        </div>
      )}
      {hooks && !hooks.length && <div className="apg-empty">{t('webhooks.none', 'No webhooks yet.')}</div>}
      <div className="apg-new">
        <div className="apg-form">
          <input className="apg-input" placeholder="https://example.com/flux-hook" aria-label={t('webhooks.urlLabel', 'Webhook URL')} value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} />
          <select className="apg-input narrow" aria-label={t('webhooks.allMyMatches', 'All my matches')} value={form.season_id} onChange={e => setForm({ ...form, season_id: e.target.value })}>
            <option value="">{t('webhooks.allMyMatches', 'All my matches')}</option>
            {seasons.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button type="button" className="apg-btn primary" disabled={!form.url.trim()} onClick={create}>{t('webhooks.add', 'Add webhook')}</button>
        </div>
        <span className="apg-label">{form.types.length ? t('webhooks.onlyTypes', 'Only these events') : t('webhooks.allEventsHint', 'All events (or pick some)')}</span>
        <div className="apg-types">
          {EVENT_TYPES.map(typeButton)}
          <button type="button" className="apg-type all" onClick={() => setForm(f => ({ ...f, types: EVENT_TYPES.every(ty => f.types.includes(ty)) ? f.types.filter(ty => !EVENT_TYPES.includes(ty)) : [...new Set([...f.types, ...EVENT_TYPES])] }))}>
            {t('webhooks.allMatchEvents', 'All match events')}
          </button>
        </div>
        {/* bracket events are opt-in: an "all events" webhook gets match events only */}
        <span className="apg-label">{t('webhooks.bracketEvents', 'Bracket events (only when picked)')}</span>
        <div className="apg-types">{BRACKET_EVENT_TYPES.map(typeButton)}</div>
        {form.types.some(ty => BRACKET_EVENT_TYPES.includes(ty)) && !form.types.some(ty => EVENT_TYPES.includes(ty)) && (
          <div className="apg-small">{t('webhooks.bracketOnlyHint', 'Only bracket events will be sent. Pick match events too (or "All match events") to get both.')}</div>
        )}
        {err && <div className="apg-error">{err}</div>}
      </div>
    </section>
  );
}
